import React, { useState, useEffect, useCallback } from 'react';

interface CollectionTaskingPanelProps {
  selectedAOI?: any;
}

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

const PRIORITIES = [
  { key: 'P1', label: 'P1 — Critical', desc: 'Every 6 hours', color: 'bg-red-600' },
  { key: 'P2', label: 'P2 — High', desc: 'Every 12 hours', color: 'bg-orange-500' },
  { key: 'P3', label: 'P3 — Standard', desc: 'Every 24 hours', color: 'bg-yellow-500' },
  { key: 'P4', label: 'P4 — Low', desc: 'Every 72 hours', color: 'bg-green-600' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'text-green-400',
  paused: 'text-yellow-400',
  completed: 'text-blue-400',
  failed: 'text-red-400',
  cancelled: 'text-gray-400',
};

const CollectionTaskingPanel: React.FC<CollectionTaskingPanelProps> = ({ selectedAOI }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('P3');
  const [justification, setJustification] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const aoiId = selectedAOI?.id || selectedAOI?.aoi_id;

  // Fetch tasks for the selected AOI
  const fetchTasks = useCallback(async () => {
    if (!aoiId) {
      setTasks([]);
      return;
    }
    setLoading(true);
    try {
      const token = getAuthToken();
      const resp = await fetch(`/api/geoint/wayback/tasking?aoi_id=${aoiId}&limit=20`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setTasks(data.tasks || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [aoiId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const createTask = async () => {
    if (!aoiId) return;
    setCreating(true);
    try {
      const token = getAuthToken();
      const body: any = {
        aoi_id: aoiId,
        priority: selectedPriority,
      };
      if (justification.trim()) {
        body.priority_override_justification = justification.trim();
      }
      if (notes.trim()) {
        body.notes = notes.trim();
      }

      const resp = await fetch('/api/geoint/wayback/tasking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        setShowCreate(false);
        setSelectedPriority('P3');
        setJustification('');
        setNotes('');
        fetchTasks();
      }
    } catch { /* ignore */ }
    setCreating(false);
  };

  const cancelTask = async (taskId: string) => {
    try {
      const token = getAuthToken();
      const resp = await fetch(`/api/geoint/wayback/tasking/${taskId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) fetchTasks();
    } catch { /* ignore */ }
  };

  const formatTime = (ts: string) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 flex items-center justify-between bg-card">
        <h3 className="text-sm font-semibold text-foreground">Collection Tasking</h3>
        {aoiId && (
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="text-[10px] px-2 py-0.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {showCreate ? 'Cancel' : '+ New Task'}
          </button>
        )}
      </div>

      {!aoiId ? (
        <div className="px-3 py-3">
          <p className="text-xs text-muted-foreground">Select an AOI to manage collection tasking.</p>
        </div>
      ) : (
        <>
          {/* Create form */}
          {showCreate && (
            <div className="px-3 py-2 border-b border-border space-y-2 bg-muted/20">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Priority</label>
                <div className="grid grid-cols-4 gap-1 mt-1">
                  {PRIORITIES.map(p => (
                    <button
                      key={p.key}
                      onClick={() => setSelectedPriority(p.key)}
                      className={`px-1.5 py-1 rounded text-[10px] font-medium transition-colors ${
                        selectedPriority === p.key
                          ? `${p.color} text-white`
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                      title={p.desc}
                    >
                      {p.key}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {PRIORITIES.find(p => p.key === selectedPriority)?.desc}
                </p>
              </div>

              {selectedPriority !== 'P3' && (
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Override Justification
                  </label>
                  <input
                    type="text"
                    value={justification}
                    onChange={e => setJustification(e.target.value)}
                    placeholder="Reason for non-standard priority..."
                    className="w-full mt-1 px-2 py-1 bg-background border border-border rounded text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              )}

              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Notes (optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Collection notes..."
                  className="w-full mt-1 px-2 py-1 bg-background border border-border rounded text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <button
                onClick={createTask}
                disabled={creating}
                className="w-full px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 transition-colors"
              >
                {creating ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          )}

          {/* Active tasks list */}
          <div className="px-3 py-2 space-y-1.5">
            {loading ? (
              <div className="flex items-center justify-center py-2">
                <div className="animate-spin h-3 w-3 border-2 border-cyan-400 border-t-transparent rounded-full" />
              </div>
            ) : tasks.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                No collection tasks for this AOI
              </p>
            ) : (
              tasks.map((task: any) => {
                const priorityConfig = PRIORITIES.find(p => p.key === task.effective_priority);
                return (
                  <div
                    key={task.task_id}
                    className="text-xs p-2 rounded border border-border hover:border-border/80"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className={`font-bold ${priorityConfig?.color?.replace('bg-', 'text-') || 'text-foreground'}`}>
                          {task.effective_priority}
                        </span>
                        <span className={`${STATUS_COLORS[task.status] || 'text-foreground'}`}>
                          {task.status}
                        </span>
                      </div>
                      {task.status === 'active' && (
                        <button
                          onClick={() => cancelTask(task.task_id)}
                          className="text-[10px] text-red-400 hover:underline"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 space-y-0.5">
                      <div>Next: {formatTime(task.scheduled_at)}</div>
                      <div>Runs: {task.execution_count || 0}</div>
                      {task.last_result?.metrics?.change_percentage != null && (
                        <div>Last change: {task.last_result.metrics.change_percentage.toFixed(1)}%</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CollectionTaskingPanel;
